import { useCallback, useEffect, useState } from "react";
import useIsAdmin from "hooks/useIsAdmin";
import { getUserRole } from "features/auth/services/auth";

// Vira `true` só no PR de virada de chave, depois que toda a v2 (design_handoff_v2)
// tiver sido validada em produção via o preview admin-only abaixo.
export const DESIGN_V2_DEFAULT = false;

const PREVIEW_STORAGE_KEY = "bp_design_v2_preview";
const PREVIEW_CHANGE_EVENT = "bp_design_v2_preview_change";

const readPreviewFlag = () => {
    try {
        return localStorage.getItem(PREVIEW_STORAGE_KEY) === "1";
    } catch {
        return false;
    }
};

const writePreviewFlag = (enabled) => {
    try {
        if (enabled) {
            localStorage.setItem(PREVIEW_STORAGE_KEY, "1");
        } else {
            localStorage.removeItem(PREVIEW_STORAGE_KEY);
        }
    } catch {
        // localStorage indisponível (ex: modo privado) — segue sem persistir a preferência.
    }
    // localStorage sozinho só notifica outras ABAS (evento "storage"), nunca a aba que
    // escreveu — sem isso, o toggle no Perfil não avisa o AccountPage (que decide v1 vs
    // v2) nem qualquer outra tela v2 aberta na mesma aba, e a mudança só apareceria após reload.
    window.dispatchEvent(new Event(PREVIEW_CHANGE_EVENT));
};

const usePreviewFlag = () => {
    const [enabled, setEnabledState] = useState(readPreviewFlag);

    useEffect(() => {
        const sync = () => setEnabledState(readPreviewFlag());
        sync();

        window.addEventListener(PREVIEW_CHANGE_EVENT, sync);
        window.addEventListener("storage", sync);

        return () => {
            window.removeEventListener(PREVIEW_CHANGE_EVENT, sync);
            window.removeEventListener("storage", sync);
        };
    }, []);

    return enabled;
};

/**
 * Decide se a tela atual deve renderizar a versão v2. Usar em cada page migrada:
 *   const showV2 = useDesignV2();
 *   return showV2 ? <DashboardV2 /> : <Dashboard />;
 *
 * Não checa `isAdmin` aqui de propósito: Login/Cadastro/Esqueci-senha rodam ANTES da
 * autenticação, onde `isAdmin` é sempre `false` por falta de token — checar isso faria
 * o preview nunca ligar nessas telas. O controle de quem PODE ligar o preview já é
 * feito no toggle em si (admin-only, ver useDesignV2Preview/DesignV2PreviewToggle);
 * uma vez ligado, ele vale pra qualquer tela v2 no mesmo navegador, logado ou não —
 * por isso o AuthContext chama `clearDesignV2Preview()` no login de quem não é admin
 * e no logout, pra não vazar o preview pra outra aluna num aparelho compartilhado.
 */
export const useDesignV2 = () => {
    const previewEnabled = usePreviewFlag();

    return DESIGN_V2_DEFAULT || previewEnabled;
};

/**
 * Apaga o preview pessoal salvo no navegador. Chamado no login/logout do AuthContext
 * quando quem loga não é admin — sem isso, o flag fica gravado no navegador (não no
 * usuário) e uma aluna que logar depois de um admin no mesmo aparelho herdaria a v2
 * ainda não liberada.
 */
export const clearDesignV2Preview = () => {
    writePreviewFlag(false);
};

// AuthContext limpa o preview no login/logout DAQUELA aba, mas o token em si é
// compartilhado por todas as abas via localStorage — se uma aluna deixa uma aba aberta
// (com o token antigo ainda válido nos closures do React) e, na mesma janela, um admin
// loga numa outra aba pra testar o preview, o "storage" abaixo pega essa troca de token
// e reavalia o preview pra aba da aluna também, mesmo sem ela ter recarregado a página.
if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
        if (event.key === "token" && getUserRole() !== "admin") {
            clearDesignV2Preview();
        }
    });
}

/**
 * Liga/desliga o preview pessoal da v2. Usado só pelo toggle admin-only no Perfil —
 * não afeta o que as alunas veem, e não interfere no DESIGN_V2_DEFAULT global.
 */
export const useDesignV2Preview = () => {
    const isAdmin = useIsAdmin();
    const enabled = usePreviewFlag();

    const setEnabled = useCallback((value) => {
        writePreviewFlag(value);
    }, []);

    return { isAdmin, enabled, setEnabled };
};
